import {
  AlertModal,
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  useOpenModal,
} from '@modusign/design-system';
import { downloadExcelForBatch } from '@modusign/openapi-client-external-document';
import moment from 'moment';
import { useState } from 'react';
import { useMutation } from 'react-query';

import { LabelSelectButton } from '@app/features/label';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import josa from '@app/libs/josa';
import workspaceService from '@app/libs/workspaceService';
import { downloadFileFromURL } from '@app/store/transport-layer/files';

interface DownloadBatchTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export default function DownloadBatchTemplateModal({
  isOpen,
  onClose,
}: DownloadBatchTemplateModalProps) {
  const openModal = useOpenModal();
  const { handleError } = useOpenErrorModal();

  const [label, setLabel] = useState<{ id: string; name: string } | undefined>(undefined);

  const { mutate: downloadFile, isLoading } = useMutation(
    async () => {
      if (!label) {
        return;
      }

      const { url } = await downloadExcelForBatch(workspaceService.getWorkspaceId(), {
        labelId: label.id,
      });
      const saveFileName = `${label.name}_일괄 업데이트 양식_${moment().format('YYMMDD')}.xlsx`;
      await downloadFileFromURL({ url, saveFileName });
    },
    {
      onSuccess: onClose,
      onError: (error: Error) => {
        if (error.message === 'No documents to build') {
          openModal((props) => (
            <AlertModal {...props} title="계약 없음" variant="warning">
              {josa(`'${label?.name}'`, '이/가')} 적용된 계약이 없습니다.
            </AlertModal>
          ));
          return;
        }

        handleError(
          { title: '다운로드 실패', error },
          { 'Forbidden permission': '더 이상 볼 수 없거나 삭제된 라벨입니다.' }
        );
      },
    }
  );

  return (
    <Modal isOpen={isOpen} maxWidth="xs" onClose={onClose}>
      <ModalTitle>설정용 엑셀 다운로드</ModalTitle>
      <ModalContent>
        <Text textAlign="center">업데이트할 계약의 라벨을 선택해 주세요.</Text>
        <Flex justifyContent="center" mt={7}>
          <LabelSelectButton
            label={label}
            nestedPopover
            width="320px"
            onChange={setLabel}
            onClear={() => {
              setLabel(undefined);
            }}
          />
        </Flex>
      </ModalContent>
      <ModalFooter>
        <Button disabled={isLoading} onClick={onClose}>
          취소
        </Button>
        <Button
          disabled={!label}
          isLoading={isLoading}
          ml={3}
          variant="primary"
          onClick={() => downloadFile()}
        >
          다운로드
        </Button>
      </ModalFooter>
    </Modal>
  );
}
